/**
 * SubstrateLogsPage
 *
 * Main page for tracking third-party substrate (shiitake) logs.
 * Provides dashboard, purchases, inductions, and overheads tabs.
 */

import { useState } from 'react'
import { PageLayout } from '@/components/layout'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSubstrateLogs } from '@/hooks/useSubstrateLogs'
import SubstrateDashboard from '@/components/substrate-logs/SubstrateDashboard'
import PurchasesList from '@/components/substrate-logs/PurchasesList'
import InductionsList from '@/components/substrate-logs/InductionsList'
import OverheadCosts from '@/components/substrate-logs/OverheadCosts'
import RunningCosts from '@/components/substrate-logs/RunningCosts'
import ShipmentsList from '@/components/substrate-logs/ShipmentsList'
import { BarChart3, ShoppingCart, Layers, Receipt, Zap, Truck } from 'lucide-react'

function getCurrentMonthRange() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const lastDay = new Date(year, now.getMonth() + 1, 0).getDate()
  return {
    start: `${year}-${month}-01`,
    end: `${year}-${month}-${String(lastDay).padStart(2, '0')}`
  }
}

export default function SubstrateLogsPage({ user, onLogout }) {
  const [periodStart, setPeriodStart] = useState(getCurrentMonthRange().start)
  const [periodEnd, setPeriodEnd] = useState(getCurrentMonthRange().end)
  const [activeTab, setActiveTab] = useState('dashboard')

  const hook = useSubstrateLogs({ periodStart, periodEnd })

  const handlePeriodChange = (start, end) => {
    setPeriodStart(start)
    setPeriodEnd(end)
  }

  return (
    <PageLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Substrate Logs</h1>
          <p className="text-muted-foreground mt-1">
            Track shiitake substrate log purchases, inductions, harvests, shipments, and profitability
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-4xl grid-cols-6">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="purchases" className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Purchases
            </TabsTrigger>
            <TabsTrigger value="inductions" className="flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Inductions
            </TabsTrigger>
            <TabsTrigger value="overheads" className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Overheads
            </TabsTrigger>
            <TabsTrigger value="running-costs" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Running Costs
            </TabsTrigger>
            <TabsTrigger value="shipments" className="flex items-center gap-2">
              <Truck className="h-4 w-4" />
              Shipments
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6">
            <SubstrateDashboard
              hook={hook}
              periodStart={periodStart}
              periodEnd={periodEnd}
              onPeriodChange={handlePeriodChange}
              onNavigateToInductions={() => setActiveTab('inductions')}
            />
          </TabsContent>

          <TabsContent value="purchases" className="mt-6">
            <PurchasesList hook={hook} />
          </TabsContent>

          <TabsContent value="inductions" className="mt-6">
            <InductionsList hook={hook} />
          </TabsContent>

          <TabsContent value="overheads" className="mt-6">
            <OverheadCosts
              hook={hook}
              periodStart={periodStart}
              periodEnd={periodEnd}
              onPeriodChange={handlePeriodChange}
            />
          </TabsContent>

          <TabsContent value="running-costs" className="mt-6">
            <RunningCosts hook={hook} />
          </TabsContent>

          <TabsContent value="shipments" className="mt-6">
            <ShipmentsList
              hook={hook}
              periodStart={periodStart}
              periodEnd={periodEnd}
              onPeriodChange={handlePeriodChange}
            />
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  )
}
